import React, { useEffect, useState, useCallback } from 'react';
import {
    aig_audit_evidence_library, aig_audit_evidence_library_audits,
    aig_audit_evidence_library_export, aig_audit_evidence_download,
} from '../services';
import AuditPagination from '../Components/AuditPagination';
import EvidenceAuditDetail from './EvidenceAuditDetail';
import EvidencePreviewModal, { isPreviewable } from './EvidencePreviewModal';
import {
    STATUS_COLORS, CATEGORY_COLORS, fmtDate, fmtSize, prettyLabel, saveBlob,
    thStyle, tdStyle, pillStyle, tileStyle, tileLabelStyle, tileValueStyle,
} from './evidenceFormat';

// Evidence grouped by audit. Clicking an audit opens its controls, the
// auditee response against each, and the files filed under them
// (EvidenceAuditDetail). "All files" keeps the flat per-file list.
// Backed by /audit/evidence-library/ — see evidence_library_views.py.
//
// A platform manager sees the whole organization; auditor, auditee and
// business owner see only the audits they are assigned to. That narrowing
// happens server-side in EvidenceLibraryService.visible_audit_ids() — this
// component never filters by role itself.
//
// Props:
//   flash(msg) — toast helper from the parent

const PAGE_SIZE = 10;

const MODE_AUDITS = 'audits';
const MODE_FILES = 'files';

export default function EvidenceLibrary({ flash }) {
    const [mode, setMode] = useState(MODE_AUDITS);
    const [openAuditId, setOpenAuditId] = useState(null);
    const [search, setSearch] = useState('');

    const [auditRows, setAuditRows] = useState([]);
    const [auditSummary, setAuditSummary] = useState({});
    const [auditPagination, setAuditPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
    const [auditPage, setAuditPage] = useState(1);

    const [fileRows, setFileRows] = useState([]);
    const [fileSummary, setFileSummary] = useState({});
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_count: 0 });
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState({ by: 'uploaded_at', dir: 'desc' });
    const [expandedId, setExpandedId] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);

    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const loadAudits = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: auditPage, page_size: PAGE_SIZE };
            if (search) params.search = search;
            const r = await aig_audit_evidence_library_audits(params);
            const d = r?.statusCode?.response_data?.data;
            setAuditRows(Array.isArray(d?.audits) ? d.audits : []);
            setAuditSummary(d?.summary || {});
            setAuditPagination(d?.pagination || { page: 1, total_pages: 1, total_count: 0 });
        } catch (e) {
            setAuditRows([]); setAuditSummary({});
            setAuditPagination({ page: 1, total_pages: 1, total_count: 0 });
        }
        setLoading(false);
    }, [search, auditPage]);

    const loadFiles = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE, sort_by: sort.by, sort_dir: sort.dir };
            if (search) params.search = search;
            const r = await aig_audit_evidence_library(params);
            const d = r?.statusCode?.response_data?.data;
            setFileRows(Array.isArray(d?.evidence) ? d.evidence : []);
            setFileSummary(d?.summary || {});
            setPagination(d?.pagination || { page: 1, total_pages: 1, total_count: 0 });
        } catch (e) {
            setFileRows([]); setFileSummary({});
            setPagination({ page: 1, total_pages: 1, total_count: 0 });
        }
        setLoading(false);
    }, [search, page, sort]);

    useEffect(() => {
        if (openAuditId) return;
        if (mode === MODE_AUDITS) loadAudits(); else loadFiles();
    }, [mode, openAuditId, loadAudits, loadFiles]);

    const toggleSort = (field) => {
        setSort(prev => ({ by: field, dir: prev.by === field && prev.dir === 'desc' ? 'asc' : 'desc' }));
        setPage(1);
    };
    const sortArrow = (field) => (sort.by === field ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

    const downloadFile = async (evidence) => {
        try {
            const r = await aig_audit_evidence_download(evidence.id);
            saveBlob(r?.statusCode || r?.data || r, evidence.file_name);
        } catch (e) { flash?.('Could not download that file.'); }
    };

    const exportCsv = async () => {
        setExporting(true);
        try {
            const r = await aig_audit_evidence_library_export(search ? { search } : null);
            saveBlob(r?.statusCode || r?.data || r, 'evidence_library.csv');
        } catch (e) { flash?.('Export failed.'); }
        setExporting(false);
    };

    if (openAuditId) {
        return <EvidenceAuditDetail auditId={openAuditId} onBack={() => setOpenAuditId(null)} flash={flash} />;
    }

    const tiles = mode === MODE_AUDITS
        ? [
            ['Audits', auditSummary.audit_count ?? 0],
            ['Evidence', auditSummary.file_count ?? 0],
            ['Controls', auditSummary.control_count ?? 0],
            ['With evidence', auditSummary.controls_with_evidence ?? 0],
            ['Missing evidence', auditSummary.controls_missing_evidence ?? 0],
        ]
        : [
            ['Files', fileSummary.total_files ?? 0],
            ['Audits covered', fileSummary.audits_covered ?? 0],
            ['Controls covered', fileSummary.controls_covered ?? 0],
        ];

    const rowsEmpty = mode === MODE_AUDITS ? !auditRows.length : !fileRows.length;

    return (
        <div className="ap-panel">
            <div style={{ marginBottom: 16 }}>
                <h3 className="ap-h3" style={{ margin: 0 }}>Evidence Library</h3>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {tiles.map(([label, value]) => (
                    <div key={label} style={tileStyle}>
                        <div style={tileLabelStyle}>{label}</div>
                        <div style={tileValueStyle}>{value}</div>
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="ap-input" style={{ flex: '2 1 240px' }} placeholder="Search file, control, audit…"
                    value={search} onChange={e => { setSearch(e.target.value); setPage(1); setAuditPage(1); }} />
            </div>

            {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--ap-text-3)', fontSize: 12 }}>Loading evidence…</div>
            ) : rowsEmpty ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--ap-text-3)', fontSize: 12 }}>
                    {search ? 'Nothing matches that search.' : 'No audits with controls or evidence yet.'}
                </div>
            ) : mode === MODE_AUDITS ? (
                <div style={{ overflowX: 'auto', border: '1px solid var(--ap-border)', borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ background: 'var(--ap-surface-2)' }}>
                                <th style={thStyle(false)}>Audit</th>
                                <th style={thStyle(false)}>Framework</th>
                                <th style={thStyle(false)}>Controls</th>
                                <th style={thStyle(false)}>Evidence coverage</th>
                                <th style={thStyle(false)}>Files</th>
                                <th style={thStyle(false)}>Contributors</th>
                                <th style={thStyle(false)}>Closure date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {auditRows.map(row => (
                                <tr key={row.audit_id} style={{ borderTop: '1px solid var(--ap-border)', cursor: 'pointer' }}
                                    onClick={() => setOpenAuditId(row.audit_id)}>
                                    <td style={tdStyle}>
                                        <div style={{ fontWeight: 700, color: 'var(--ap-text)' }}>{row.audit_number}</div>
                                        <div style={{ color: 'var(--ap-text-3)', fontSize: 10 }}>{row.title}</div>
                                    </td>
                                    <td style={tdStyle}>{row.framework || '-'}</td>
                                    <td style={{ ...tdStyle, fontWeight: 700 }}>{row.control_count}</td>
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                        <span style={{ fontWeight: 700, color: 'var(--ap-text)' }}>
                                            {row.controls_with_evidence}/{row.control_count}
                                        </span>
                                        {row.controls_missing_evidence > 0 && (
                                            <span style={{ ...pillStyle('#d97706'), marginLeft: 6 }}>
                                                {row.controls_missing_evidence} missing
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ ...tdStyle, fontWeight: 700 }}>{row.file_count}</td>
                                    {/* <td style={tdStyle}>
                                        {row.non_conformity_count
                                            ? <span style={pillStyle(CATEGORY_COLORS.major)}>{row.non_conformity_count}</span>
                                            : <span style={{ color: 'var(--ap-text-3)' }}>-</span>}
                                    </td> */}
                                    <td style={tdStyle}>{row.contributor_count}</td>
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(row.last_upload)}</td>
                                   
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{ overflowX: 'auto', border: '1px solid var(--ap-border)', borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ background: 'var(--ap-surface-2)' }}>
                                <th onClick={() => toggleSort('file_name')} style={thStyle(true)}>File{sortArrow('file_name')}</th>
                                <th onClick={() => toggleSort('control_ref')} style={thStyle(true)}>Control{sortArrow('control_ref')}</th>
                                <th onClick={() => toggleSort('audit_number')} style={thStyle(true)}>Audit{sortArrow('audit_number')}</th>
                                <th style={thStyle(false)}>Status</th>
                                <th style={thStyle(false)}>Category</th>
                                <th style={thStyle(false)}>Uploaded by</th>
                                <th onClick={() => toggleSort('uploaded_at')} style={thStyle(true)}>Date{sortArrow('uploaded_at')}</th>
                                <th onClick={() => toggleSort('file_size')} style={thStyle(true)}>Size{sortArrow('file_size')}</th>
                                <th style={thStyle(false)}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {fileRows.map(row => {
                                const isExpanded = expandedId === row.id;
                                return (
                                    <React.Fragment key={row.id}>
                                        <tr style={{ borderTop: '1px solid var(--ap-border)', cursor: 'pointer' }}
                                            onClick={() => setExpandedId(isExpanded ? null : row.id)}>
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 600, color: 'var(--ap-text)' }}>{row.file_name}</div>
                                                {row.description && row.description !== row.file_name && (
                                                    <div style={{ color: 'var(--ap-text-3)', fontSize: 10, marginTop: 2 }}>{row.description}</div>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 700 }}>{row.control?.control_ref || '-'}</div>
                                                <div style={{ color: 'var(--ap-text-3)', fontSize: 10 }}>{row.control?.title || ''}</div>
                                            </td>
                                            <td style={tdStyle}>
                                                <div style={{ fontWeight: 600 }}>{row.audit?.audit_number || '-'}</div>
                                                <div style={{ color: 'var(--ap-text-3)', fontSize: 10 }}>{row.framework?.name || ''}</div>
                                            </td>
                                            <td style={tdStyle}>
                                                {row.response?.compliance_status
                                                    ? <span style={pillStyle(STATUS_COLORS[row.response.compliance_status])}>{prettyLabel(row.response.compliance_status)}</span>
                                                    : <span style={{ color: 'var(--ap-text-3)' }}>-</span>}
                                            </td>
                                            <td style={tdStyle}>
                                                {row.response?.category
                                                    ? <span style={pillStyle(CATEGORY_COLORS[row.response.category])}>{prettyLabel(row.response.category)}</span>
                                                    : <span style={{ color: 'var(--ap-text-3)' }}>-</span>}
                                            </td>
                                            <td style={{ ...tdStyle, color: 'var(--ap-text-2)' }}>{row.uploaded_by?.email || '-'}</td>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtDate(row.uploaded_at)}</td>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{fmtSize(row.file_size)}</td>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                {isPreviewable(row) && (
                                                    <button className="ap-btn ap-btn--ghost" style={{ marginRight: 6 }}
                                                        onClick={e => { e.stopPropagation(); setPreviewFile(row); }}>View</button>
                                                )}
                                                <button className="ap-btn ap-btn--ghost" onClick={e => { e.stopPropagation(); downloadFile(row); }}>Download</button>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr style={{ background: 'var(--ap-surface-2)' }}>
                                                <td colSpan={9} style={{ padding: '12px 14px' }}>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                                                        {[
                                                            ['Non-conformance', row.response?.non_conformance],
                                                            ['Correction', row.response?.correction],
                                                            ['Root cause', row.response?.root_cause],
                                                            ['Corrective action', row.response?.corrective_action],
                                                            ['Planned closure', row.response?.planned_closure],
                                                            ['Domain', row.control?.domain],
                                                            ['Business unit', row.audit?.business_unit],
                                                            ['Audit stage', prettyLabel(row.audit?.current_stage)],
                                                        ].map(([label, value]) => (
                                                            <div key={label}>
                                                                <div style={tileLabelStyle}>{label}</div>
                                                                <div style={{ fontSize: 11, color: 'var(--ap-text-2)', marginTop: 2, whiteSpace: 'pre-wrap' }}>{value || '-'}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {mode === MODE_AUDITS && auditPagination.total_pages > 1 && (
                <div style={{ marginTop: 12 }}>
                    <AuditPagination
                        page={auditPagination.page}
                        totalPages={auditPagination.total_pages}
                        totalCount={auditPagination.total_count}
                        onPageChange={setAuditPage}
                    />
                </div>
            )}

            {mode === MODE_FILES && pagination.total_pages > 1 && (
                <div style={{ marginTop: 12 }}>
                    <AuditPagination
                        page={pagination.page}
                        totalPages={pagination.total_pages}
                        totalCount={pagination.total_count}
                        onPageChange={setPage}
                    />
                </div>
            )}

            {previewFile && (
                <EvidencePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} flash={flash} />
            )}
        </div>
    );
}
